/**
 *  @class
 *  @function FacetsToggle
 */
if (!customElements.get('facets-toggle')) {
  class FacetsToggle extends HTMLElement {
    connectedCallback() {
      this.drawer = document.getElementById('Facet-Drawer');

      this.addEventListener('click', (e) => {
        e.preventDefault();
        if (OverlayManager.isOpen(this.drawer)) {
          OverlayManager.close(this.drawer);
        } else {
          OverlayManager.open(this.drawer);
        }
      });

      setTimeout(() => {
        window.dispatchEvent(new Event('resize.resize-select'));
      }, 10);
    }
  }
  customElements.define('facets-toggle', FacetsToggle);
}


class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 200);

    this.querySelector('form').addEventListener('input', this.debouncedOnSubmit.bind(this));
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    FacetFiltersForm.latestSearchParams = searchParams;
    const sections = FacetFiltersForm.getSections();
    const container = document.getElementsByClassName('thb-filter-count');
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');

    for (var item of container) {
      item.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = element => element.url === url;

      if (FacetFiltersForm.filterData.some(filterDataUrl)) {
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, searchParams);
      } else {
        FacetFiltersForm.renderSectionFromFetch(url, event, searchParams);
      }
    });
    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);

    FacetFiltersForm.publishFacetUpdate(searchParams);
  }

  // shopify:collection:update / shopify:search:update — filters or sort changed
  static publishFacetUpdate(searchParams) {
    const SE = window.Shopify && window.Shopify.StandardEvents;
    if (!SE) return;
    const params = new URLSearchParams(searchParams);

    if (theme.settings.collection_id) {
      publishStandardEvent('CollectionUpdateEvent', {
        collection: { id: theme.settings.collection_id },
        filters: SE.CollectionUpdateEvent.parseProductFilters(params),
        sortKey: SE.CollectionUpdateEvent.getSortKey(params)
      });
    } else if (window.location.pathname.indexOf(theme.routes.search_url) !== -1) {
      publishStandardEvent('SearchUpdateEvent', {
        searchTerm: params.get('q') || new URLSearchParams(window.location.search).get('q') || '',
        filters: SE.SearchUpdateEvent.parseProductFilters(params),
        sortKey: SE.SearchUpdateEvent.getSortKey(params)
      });
    }
  }

  static renderSectionFromFetch(url, event, searchParams) {
    if (FacetFiltersForm.abortController) {
      FacetFiltersForm.abortController.abort();
    }
    FacetFiltersForm.abortController = new AbortController();

    fetch(url, { signal: FacetFiltersForm.abortController.signal })
      .then(response => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, {
          html,
          url
        }];
        // Stale guard: a newer filter change may have started (and even
        // resolved) while this fetch was in flight. Drop this response
        // BEFORE the view transition fires, so an old grid never flashes
        // in front of / after the current one.
        if (searchParams !== FacetFiltersForm.latestSearchParams) return;
        withViewTransition(() => {
          FacetFiltersForm.renderFilters(html, event);
          FacetFiltersForm.renderProductGridContainer(html);
          FacetFiltersForm.renderProductCount(html);
        });
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        console.error(error);
      });
  }

  static renderSectionFromCache(filterDataUrl, event, searchParams) {
    if (searchParams !== FacetFiltersForm.latestSearchParams) return;
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    withViewTransition(() => {
      FacetFiltersForm.renderFilters(html, event);
      FacetFiltersForm.renderProductGridContainer(html);
      FacetFiltersForm.renderProductCount(html);
    });
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer').innerHTML;

    let top = document.getElementById('ProductGridContainer').getBoundingClientRect().top + document.documentElement.scrollTop - parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) - 30;

    window.scrollTo({
      top: top,
      left: 0,
      behavior: "smooth"
    });
  }

  static renderProductCount(html) {
    const countHtml = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount');
    const container = document.getElementsByClassName('thb-filter-count');

    if (countHtml) {
      for (var item of container) {
        item.innerHTML = countHtml.innerHTML;
        item.classList.remove('loading');
      }
    }

  }

  // Each layout renders its own complete copy of the filters:
  //   #FacetFiltersForm       -> desktop sidebar
  //   #FacetFiltersForm-bar   -> horizontal bar
  //   #FacetFiltersFormMobile -> mobile drawer (rendered OUTSIDE #ProductGridContainer,
  //                              so renderProductGridContainer never refreshes it)
  //
  // The stock theme resolved each re-rendered row with a document-wide
  // querySelector on data-index, which only ever hits the FIRST copy in the
  // document, and it skipped the clicked row's data-index in EVERY form.
  // Result: tick "In Stock" in the sidebar and the drawer's copy of that same
  // row is never updated -- it stays unchecked. The next click that happens to
  // land on the drawer form then serializes a form where availability is off,
  // silently dropping the filter.
  //
  // Fix: refresh every form from its OWN markup (ids differ per form, so rows
  // must never be copied across forms), and only leave untouched the exact row
  // element the user is interacting with -- its twins in the other forms are
  // refreshed like any other row.
  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const clickedRow = event && event.target ? event.target.closest('collapsible-row') : null;
    let countsToRender = null;

    ['FacetFiltersForm', 'FacetFiltersForm-bar', 'FacetFiltersFormMobile'].forEach((formId) => {
      const sourceForm = parsedHTML.getElementById(formId);
      const liveForm = document.getElementById(formId);
      if (!sourceForm || !liveForm) return;

      sourceForm.querySelectorAll('collapsible-row').forEach((source) => {
        const target = liveForm.querySelector(`collapsible-row[data-index="${source.dataset.index}"]`);
        if (!target) return;
        if (target === clickedRow) {
          countsToRender = source;
          return;
        }
        target.innerHTML = source.innerHTML;
      });
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender && clickedRow) FacetFiltersForm.renderCounts(countsToRender, clickedRow);
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-filter-count'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({
      searchParams
    }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      }];
  }

  // Multiple <facet-filters-form> instances can be live at once (desktop
  // sidebar, horizontal bar, mobile drawer) but only one of them actually
  // carries real filter checkboxes for a given layout — the others are
  // sort-only or hidden-state-sync duplicates. Pick whichever one has real
  // filter inputs so a "current complete state" read is never taken from
  // an empty/hidden-only form.
  static getAuthoritativeFilterForm() {
    const candidates = [
      document.getElementById('FacetFiltersForm'),
      document.getElementById('FacetFiltersForm-bar'),
      document.getElementById('FacetFiltersFormMobile')
    ].filter(Boolean);
    return candidates.find((formEl) => formEl.querySelector('input[name^="filter."]')) || candidates[0] || null;
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const form = event.target.closest('form');
    const searchParams = new URLSearchParams(new FormData(form));

    // Defensive merge: if a filter param is part of our last confirmed
    // complete state but THIS form has no control for it at all (eg. the
    // click landed on a different facet-filters-form instance than the
    // one that owns that control), don't let it silently drop. Any key
    // this form CAN represent is left entirely to the form's own current
    // state, checked or not — this never overrides an intentional uncheck.
    const latestParams = new URLSearchParams(FacetFiltersForm.latestSearchParams);
    latestParams.forEach((value, key) => {
      if (!form.querySelector(`[name="${key}"]`)) {
        searchParams.append(key, value);
      }
    });

    if (searchParams.get('filter.v.price.gte') === "0.00") {
      searchParams.delete('filter.v.price.gte');
    }
    if (document.querySelector('.price_slider')) {
      if (searchParams.get('filter.v.price.lte') === parseFloat(document.querySelector('.price_slider').dataset.max).toFixed(2)) {
        searchParams.delete('filter.v.price.lte');
      }
    }
    FacetFiltersForm.renderPage(searchParams.toString(), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();

    const href = event.currentTarget.href;
    const pillParams = new URLSearchParams(href.indexOf('?') === -1 ? '' : href.slice(href.indexOf('?') + 1));

    // The pill's href is a snapshot Shopify computed server-side at the
    // LAST render: "all filters that were active then, minus this one."
    // If a checkbox has been ticked since (still pending in the 200ms
    // debounce window), that pending selection exists nowhere in this
    // href, and applying it wholesale would silently drop it. Instead:
    //   1) read the CURRENT live filter state (picks up any pending click)
    //   2) diff the pill's href against our last CONFIRMED complete state
    //      to find exactly which key/value this pill is removing
    //   3) remove only that key/value from the current live state
    const authoritativeForm = FacetFiltersForm.getAuthoritativeFilterForm();
    const liveParams = authoritativeForm
      ? new URLSearchParams(new FormData(authoritativeForm))
      : new URLSearchParams(FacetFiltersForm.latestSearchParams);

    const confirmedParams = new URLSearchParams(FacetFiltersForm.latestSearchParams);
    confirmedParams.forEach((value, key) => {
      if (!pillParams.getAll(key).includes(value)) {
        const remaining = liveParams.getAll(key).filter((v) => v !== value);
        liveParams.delete(key);
        remaining.forEach((v) => liveParams.append(key, v));
      }
    });

    FacetFiltersForm.renderPage(liveParams.toString());
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
FacetFiltersForm.latestSearchParams = window.location.search.slice(1);
FacetFiltersForm.abortController = null;
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();


class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('a').forEach((item) => {
      item.addEventListener('click', (event) => {
        event.preventDefault();
        const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
        form.onActiveFilterClick(event);
      });
    });
  }
}

customElements.define('facet-remove', FacetRemove);

/**
 *  @class
 *  @function PriceSlider
 */
class PriceSlider extends HTMLElement {

  constructor() {
    super();
  }
  connectedCallback() {
    let slider = this.querySelector('.price_slider'),
      amounts = this.querySelector('.price_slider_amount'),
      args = {
        start: [parseFloat(slider.dataset.minValue || 0), parseFloat(slider.dataset.maxValue || slider.dataset.max)],
        connect: true,
        step: 10,
        direction: document.dir,
        handleAttributes: [
          { 'aria-label': 'lower' },
          { 'aria-label': 'upper' },
        ],
        range: {
          'min': 0,
          'max': parseFloat(slider.dataset.max)
        }
      },
      event = new CustomEvent('input'),
      form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');

    if (slider.classList.contains('noUi-target')) {
      slider.noUiSlider.destroy();
    }
    noUiSlider.create(slider, args);

    slider.noUiSlider.on('update', function (values) {
      amounts.querySelector('.field__input_min').value = values[0];
      amounts.querySelector('.field__input_max').value = values[1];
    });
    slider.noUiSlider.on('change', function (values) {
      form.querySelector('form').dispatchEvent(event);
    });
  }
}
customElements.define('price-slider', PriceSlider);

(function () {
  const updateShift = (label) => {
    const content = label.querySelector('.facet-tooltip__content');
    if (!content) return;
    label.style.setProperty('--tooltip-shift', '0px');
    const rect = content.getBoundingClientRect();
    const gutter = 8;
    const vw = document.documentElement.clientWidth;
    let shift = 0;
    if (rect.left < gutter) {
      shift = Math.round(gutter - rect.left);
    } else if (rect.right > vw - gutter) {
      shift = Math.round(vw - gutter - rect.right);
    }
    label.style.setProperty('--tooltip-shift', shift + 'px');
  };

  document.addEventListener('mouseover', (e) => {
    if (!e.target.closest) return;
    const label = e.target.closest('.facet-checkbox--tooltip');
    if (!label || label._tooltipHovered) return;
    label._tooltipHovered = true;
    updateShift(label);
  });

  document.addEventListener('mouseout', (e) => {
    if (!e.target.closest) return;
    const label = e.target.closest('.facet-checkbox--tooltip');
    if (!label) return;
    if (!label.contains(e.relatedTarget)) {
      label._tooltipHovered = false;
    }
  });
})();
